/**
 * Routage.
 *
 * Une route par entrée de la barre latérale ; les pages d'usage de l'app
 * vivent sous `/usage`, Analytics est le site vu par GA4. La période vit dans la query string, donc elle
 * survit au passage d'une page à l'autre (cf. `usePeriodParam`).
 *
 * Les anciennes adresses (`/ventes`, `/apprentissage`…) redirigent vers leur
 * nouvelle place EN GARDANT la query string : un signet ou un lien partagé
 * garde sa période.
 */

import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { ProtectedRoute } from './components/ProtectedRoute';
import { AccountPage } from './pages/AccountPage';
import { AnalysesPage } from './pages/AnalysesPage';
import { BtobPage } from './pages/BtobPage';
import { ChargesPage } from './pages/ChargesPage';
import { CatalogPage } from './pages/CatalogPage';
import { SavPage } from './pages/SavPage';
import { FraisPage } from './pages/FraisPage';
import { Ga4Page } from './pages/Ga4Page';
import { DashboardPage } from './pages/DashboardPage';
import { EngagementPage } from './pages/EngagementPage';
import { LearningPage } from './pages/LearningPage';
import { LoginPage } from './pages/LoginPage';
import { MarkersPage } from './pages/MarkersPage';
import { PersonalizationPage } from './pages/PersonalizationPage';
import { SalesPage } from './pages/SalesPage';
import { SessionsPage } from './pages/SessionsPage';
import { SubscriptionPage } from './pages/SubscriptionPage';

/** Redirection qui conserve la période (`?from=…&to=…`). */
function RedirectKeepingSearch({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={{ pathname: to, search }} replace />;
}

const guard = (page: ReactNode) => <ProtectedRoute>{page}</ProtectedRoute>;

/** Anciennes adresses → nouvelles. */
const LEGACY_REDIRECTS: Record<string, string> = {
  // Ancienne page Trafic, puis onglet GA4 d'Analytics : devenue la page Analytics.
  '/trafic': '/analytics',
  // L'usage de l'app a quitté Analytics pour son entrée « Usage app ».
  '/analytics/site': '/analytics',
  '/analytics/apprentissage': '/usage/apprentissage',
  '/analytics/sessions': '/usage/sessions',
  '/analytics/catalogue': '/usage/catalogue',
  '/analytics/retention': '/usage/retention',
  '/analytics/abonnement': '/usage/abonnement',
  '/analytics/personnalisation': '/usage/personnalisation',
  '/analytics/analyses': '/usage/analyses',
  '/ventes': '/shopify',
  '/apprentissage': '/usage/apprentissage',
  '/sessions': '/usage/sessions',
  '/catalogue': '/usage/catalogue',
  '/retention': '/usage/retention',
  '/abonnement': '/usage/abonnement',
  '/personnalisation': '/usage/personnalisation',
  '/analyses': '/usage/analyses',
};

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route path="/" element={guard(<SalesPage view="summary" />)} />
      <Route path="/bf26" element={guard(<SalesPage view="bf26" />)} />
      <Route path="/marqueurs" element={guard(<MarkersPage />)} />

      {/* Shops */}
      <Route path="/shopify" element={guard(<SalesPage view="shopify" />)} />
      <Route path="/souscriptions" element={guard(<SalesPage view="subscriptions" />)} />
      <Route path="/amazon" element={guard(<SalesPage view="amazon" />)} />
      <Route path="/btob" element={guard(<BtobPage />)} />

      {/* Spends */}
      <Route path="/ads" element={guard(<SalesPage view="ads" />)} />
      <Route path="/frais" element={guard(<FraisPage />)} />
      <Route path="/charges" element={guard(<ChargesPage />)} />

      {/* Customers */}
      <Route path="/analytics" element={guard(<Ga4Page />)} />
      <Route path="/usage" element={guard(<DashboardPage />)} />
      <Route path="/usage/apprentissage" element={guard(<LearningPage />)} />
      <Route path="/usage/sessions" element={guard(<SessionsPage />)} />
      <Route path="/usage/catalogue" element={guard(<CatalogPage />)} />
      <Route path="/usage/retention" element={guard(<EngagementPage />)} />
      <Route path="/usage/abonnement" element={guard(<SubscriptionPage />)} />
      <Route path="/usage/personnalisation" element={guard(<PersonalizationPage />)} />
      <Route path="/usage/analyses" element={guard(<AnalysesPage />)} />
      <Route path="/sav" element={guard(<SavPage />)} />
      <Route path="/compte" element={guard(<AccountPage />)} />

      {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
        <Route key={from} path={from} element={<RedirectKeepingSearch to={to} />} />
      ))}

      {/* Tout le reste ramène au résumé. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
